import { Plus, Edit2, Trash2, Loader2, Search, Users, ChevronRight } from "lucide-react";
import { BusinessService } from "../../types/businessService";
import { Tax } from "../../services/businessServicesApi";
import { cn } from "../../lib/utils";
import { Button } from "../ui/Button";
import { useAuth } from "../../auth/AuthContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";

interface ServicesPanelProps {
  services: BusinessService[];
  categoryName: string;
  onServiceAdd: () => void;
  onServiceEdit: (service: BusinessService) => void;
  onServiceDelete: (id: string, name: string) => void;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  taxes?: Tax[];
}

export const ServicesPanel = ({
  services,
  categoryName,
  onServiceAdd,
  onServiceEdit,
  onServiceDelete,
  isLoading = false,
  error,
  onRetry,
  taxes = [],
}: ServicesPanelProps) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  return (
    <div className="flex-1 rounded-xl lg:border lg:border-gray-200 lg:shadow-sm bg-transparent lg:bg-white overflow-hidden flex flex-col lg:-mr-3">
      <div className="flex-1 lg:overflow-y-auto relative p-4 lg:p-0 lg:pr-3">
        {/* Full-width purple bar from first paint so header is never white during loading */}
        <div className="hidden lg:block sticky top-0 z-[9] h-[57px] w-full min-w-full bg-[#885ABB] border-b border-gray-100 shrink-0 -mb-[57px] pointer-events-none" aria-hidden />
        <table className="w-full min-w-full border-collapse table-fixed relative z-10">
          {/* Only show column labels after load to avoid jumbled/overlapping header during loading */}
          <thead
            className={cn(
              "sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 w-full relative after:content-[''] after:absolute after:left-full after:top-0 after:bottom-0 after:w-[calc(0.75rem+1px)] after:bg-[#885ABB] after:block after:border-b after:border-gray-100",
              isLoading ? "hidden" : "hidden lg:table-header-group"
            )}
          >
            <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4 w-full min-w-full">
              <th className="col-span-3 text-left font-bold pl-4">Service Name</th>
              <th className="col-span-1 text-left font-bold">Category</th>
              <th className="col-span-1 text-left font-bold">Net Price</th>
              <th className="col-span-1 text-left font-bold">Tax</th>
              <th className="col-span-1 text-left font-bold">Gross Price</th>
              <th className="col-span-2 text-left font-bold">Employees</th>
              <th className="col-span-1 text-center font-bold lg:min-w-[5rem]">Status</th>
              <th className="col-span-2 text-right font-bold pr-4 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0">
            {isLoading ? (
              <tr className="h-64 w-full flex items-center justify-center lg:table-row">
                <td colSpan={12} className="h-full">
                  <div className="flex h-full w-full items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr className="h-64 w-full flex items-center justify-center lg:table-row">
                <td colSpan={12} className="h-full">
                  <div className="flex flex-col h-full w-full items-center justify-center text-center px-6">
                    <p className="text-lg font-semibold text-gray-900 mb-2">
                      Unable to load services
                    </p>
                    <p className="text-gray-500 mb-6">{error}</p>
                    {onRetry && (
                      <button
                        onClick={onRetry}
                        className="px-6 py-2 rounded-lg bg-[#885ABB] text-white font-medium hover:bg-[#6f47a2]"
                      >
                        Try Again
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : services.length === 0 ? (
              <tr className="flex flex-col h-64 text-gray-400 w-full lg:grid lg:grid-cols-12">
                <td className="col-span-12 w-full h-full flex flex-col items-center justify-center">
                  <Search className="w-12 h-12 mb-3 opacity-20 mx-auto" />
                  <p className="text-lg font-medium text-gray-500">
                    No services found
                  </p>
                  <p className="text-sm">Try adjusting your search or filters</p>
                </td>
              </tr>
            ) : (
              <>
                {services.map((service, idx) => (
                  <tr
                    key={service.id}
                    className="flex flex-col lg:grid lg:grid-cols-12 gap-4 lg:gap-x-3 items-start lg:items-center px-6 py-5 lg:py-4 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full relative"
                  >
                    {/* Service Name */}
                    <td className="lg:col-span-3 font-bold text-gray-900 w-full lg:w-auto lg:pl-4">
                      {service.name}
                      <span className="block text-xs font-normal text-gray-500 mt-0.5">
                        {service.durationLabel}
                      </span>
                    </td>

                    <td className="lg:col-span-1 text-gray-700 truncate w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0 pr-4">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Category</span>
                      <span className="font-medium lg:font-normal truncate block" title={service.categoryName || ""}>
                        {service.categoryName || "—"}
                      </span>
                    </td>

                    {(() => {
                      const effectivePrice =
                        service.discountPrice && service.discountPrice < service.price
                          ? service.discountPrice
                          : service.price;

                      let totalTaxAmount = 0;
                      const taxDetails: { id: number; name: string; percent: number; amount: number }[] = [];

                      if (service.taxes && service.taxes.length > 0) {
                        service.taxes.forEach((taxId) => {
                          const tax = taxes.find((t) => String(t.id) === String(taxId));
                          if (tax) {
                            const amount = (effectivePrice * tax.percent) / 100;
                            totalTaxAmount += amount;
                            taxDetails.push({ ...tax, amount });
                          }
                        });
                      }

                      const grossPrice = effectivePrice + totalTaxAmount;

                      return (
                        <>
                          {/* Net Price Column */}
                          <td className="lg:col-span-1 lg:font-medium text-gray-900 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0 pr-2">
                            <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Net Price</span>
                            <div className="flex flex-col">
                              {service.discountPrice && service.discountPrice < service.price ? (
                                <>
                                  <span className="line-through text-gray-400 text-[10px]">
                                    {currencySymbol}{service.price.toFixed(2)}
                                  </span>
                                  <span className="font-bold text-sm">
                                    {currencySymbol}{service.discountPrice.toFixed(2)}
                                  </span>
                                </>
                              ) : (
                                <span className="font-medium text-sm">
                                  {currencySymbol}{service.price.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Tax Column */}
                          <td className="lg:col-span-1 text-gray-700 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0 pr-4">
                            <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase mb-1">Tax</span>
                            <div className="flex flex-col lg:items-start items-end">
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium lg:font-normal whitespace-nowrap">
                                  {currencySymbol}{totalTaxAmount.toFixed(2)}
                                </span>
                                {taxDetails.length === 0 && (
                                  <span className="text-[10px] text-gray-400">(0%)</span>
                                )}
                              </div>
                              {taxDetails.length > 0 && (
                                <div className="flex flex-wrap lg:justify-start justify-end gap-x-2 text-[10px] text-gray-400 leading-tight mt-0.5">
                                  {taxDetails.map((tax, i) => (
                                    <span key={i} className="whitespace-nowrap">
                                      {tax.name}: {currencySymbol}{tax.amount.toFixed(2)} ({tax.percent}%)
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>

                          {/* Gross Price Column */}
                          <td className="lg:col-span-1 font-bold lg:font-medium text-[#885ABB] w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0 pr-4">
                            <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Gross Price</span>
                            <span className="font-bold text-sm lg:text-base whitespace-nowrap">
                              {currencySymbol}{grossPrice.toFixed(2)}
                            </span>
                          </td>
                        </>
                      );
                    })()}

                    <td className="lg:col-span-2 text-gray-700 w-full lg:w-auto flex lg:block justify-between items-start lg:items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0 gap-4">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase shrink-0 pt-1">Employees</span>
                      <div className="font-medium lg:font-normal text-right lg:text-left break-words lg:truncate flex-1 min-w-0 flex flex-wrap lg:flex-nowrap items-center gap-1">
                        {service.staffNames.length > 0 ? (
                          <>
                            <span className="truncate hidden lg:inline">
                              {service.staffNames.length > 3 ? (
                                <>
                                  {service.staffNames.slice(0, 3).join(", ")}
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button className="ml-1.5 text-[#885ABB] font-bold hover:underline inline-flex items-center gap-0.5">
                                        View all
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-64 p-3" align="start">
                                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                                        <Users className="w-4 h-4 text-[#885ABB]" />
                                        <span className="text-sm font-bold text-[#402B69]">Assigned Employees</span>
                                      </div>
                                      <div className="space-y-1.5 max-h-48 overflow-y-auto custom-scrollbar">
                                        {service.staffNames.map((name, i) => (
                                          <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-purple-50 text-gray-700 text-xs font-medium group transition-colors">
                                            <div className="w-1.5 h-1.5 rounded-full bg-[#885ABB]/40 group-hover:bg-[#885ABB]" />
                                            {name}
                                          </div>
                                        ))}
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                </>
                              ) : (
                                service.staffNames.join(", ")
                              )}
                            </span>
                            <span className="lg:hidden">
                              {service.staffNames.join(", ")}
                            </span>
                          </>
                        ) : (
                          "Unassigned"
                        )}
                      </div>
                    </td>

                    <td className="lg:col-span-1 text-center w-full lg:w-auto lg:min-w-[5rem] flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Status</span>
                      <span
                        className={cn(
                          "inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-bold border shrink-0",
                          service.status === "active"
                            ? "bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]"
                            : "bg-gray-100 text-gray-600 border-gray-200"
                        )}
                      >
                        {service.status === "active" ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </td>

                    <td className="lg:col-span-2 flex items-center justify-end gap-3 w-full lg:w-auto mt-4 lg:mt-0 pt-4 lg:pt-0 border-t border-gray-100 lg:border-0 lg:pl-4 pr-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onServiceEdit(service)}
                        className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-blue-600 border-blue-200 hover:bg-blue-50 gap-2 font-bold lg:font-semibold shrink-0"
                      >
                        <Edit2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                        <span className="lg:hidden">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onServiceDelete(service.id, service.name)}
                        className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-red-600 border-red-200 hover:bg-red-50 gap-2 font-bold lg:font-semibold shrink-0"
                      >
                        <Trash2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                        <span className="lg:hidden">Delete</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
